import {
  EmptyTable,
  ErrorTable,
  LoadingTable,
} from "@/core/components/shared/data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { Switch } from "@/core/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/core/components/ui/table";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { NotificationPreferenceDto } from "@/features/notification/api/dto";
import { useNotificationPreferences } from "@/features/notification/api/use-notification-preferences";
import { useUpdateNotificationPreference } from "@/features/notification/api/use-update-notification-preference";
import { useNotificationsTranslation } from "@/features/notification/i18n/use-notifications-translation";
import { NOTIFICATION_CHANNELS } from "@/features/shared/api/enums";

/**
 * Préférences d'une identité (`GET /NotificationPreferences/party/{partyId}`)
 * — le service renvoie systématiquement les trois canaux (SMS/EMAIL/PUSH),
 * activés par défaut. Chaque ligne pilote un `PUT .../party/{partyId}/{channel}`
 * (attention : `isEnabled` est un booléen NON NULLABLE en query — l'omettre
 * équivaut à désactiver, guide §6.7).
 */
export function NotificationPreferencesSection({ partyId }: { partyId: string }) {
  const { t } = useNotificationsTranslation();
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useNotificationPreferences(partyId);
  const update = useUpdateNotificationPreference();

  // Le service peut renvoyer une seule entrée si tous canaux partagent la
  // même préférence — on complète par défaut à activé pour les canaux
  // absents (le contrat garantit qu'ils le sont côté back).
  const byChannel = new Map<string, NotificationPreferenceDto>();
  for (const pref of data ?? []) {
    byChannel.set(pref.channel, pref);
  }

  const onToggle = (channel: (typeof NOTIFICATION_CHANNELS)[number], next: boolean) => {
    update.mutate(
      { partyId, channel, isEnabled: next },
      {
        onSuccess: () =>
          notifSilence(t("notifications.preferences.feedback.updated")),
        onError: (mutationError) => notifSilenceError(mutationError.message),
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("notifications.preferences.channels.title")}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {t("notifications.preferences.identityWarning")}
        </p>
        <p className="notranslate text-xs text-muted-foreground" translate="no">
          partyId : {partyId}
        </p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("notifications.preferences.channels.title")}</TableHead>
              <TableHead className="text-right">
                {t("notifications.preferences.channelEnabled")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <LoadingTable numberOfColumns={2} />
            ) : error ? (
              <ErrorTable
                error={{ message: error.message, refetch: () => void refetch() }}
              />
            ) : !data ? (
              <EmptyTable />
            ) : (
              NOTIFICATION_CHANNELS.map((channel) => {
                const pref = byChannel.get(channel);
                // Défaut : activé (comportement documenté du service).
                const enabled = pref?.isEnabled ?? true;
                const pending =
                  update.isPending &&
                  update.variables?.partyId === partyId &&
                  update.variables?.channel === channel;
                return (
                  <TableRow key={channel}>
                    <TableCell>
                      <span className="notranslate font-medium">
                        {t(`notifications.preferences.channels.${channel}`)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Switch
                        checked={enabled}
                        disabled={pending}
                        onCheckedChange={(next) => onToggle(channel, next)}
                        aria-label={
                          enabled
                            ? t("notifications.preferences.channelEnabled")
                            : t("notifications.preferences.channelDisabled")
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
